import { withManagedForm } from "@/application/components/admin/connections/managedForm";
import { runStepByStepVerification } from "@/application/components/admin/connections/connectionVerification";

/**
 * @param {Object} params
 * @param {string} params.modalShow
 * @param {string} params.connectionType
 * @param {Object[]} params.verificationSteps
 * @returns {ManagedForm&ManagedFormChild&{[p: string]: *}}
 */
export function editApiCredentialsForm({
  connectionDisabledBadCredentials = false,
  connectionType,
  modalShow,
  verificationSteps = [],
}) {
  const form = {
    connectionDisabledBadCredentials,
    connectionType,
    verificationSteps,
    currentSettings: null,
    currentVerificationStep: null,
    showChangedWarning: true,
    forceClose: false,
    apiError: null,
    copiedWith: null,
    initChild() {
      this.beforeHide = this.beforeHide.bind(this);
      this.setupEventListeners = this.setupEventListeners.bind(this);
      this.setupEventListeners(this[modalShow]);
      this.$watch(modalShow, this.setupEventListeners);
    },
    beforeHide(event) {
      if (this.isDirty && !this.forceClose) {
        // We can call this without an event to allow the "cancel" button to work
        if (event) {
          event.preventDefault();
        }
        this.showChangedWarning = true;
        return;
      } else if (this.isDirty && this.forceClose) {
        // reset the settings if we're not saving
        this.settings = this.currentSettings;
        this[modalShow] = false;
        // reset dirty state for form fields
        for (const el of this.$el.elements) {
          const key = this.nameToKey(el.name);
          if (typeof this[key] !== "undefined" && this.fields[key]) {
            this[key] = this.fields[key].initialValue;
          }
        }
      } else {
        this[modalShow] = false;
      }
      // Clear secret fields on close regardless of if we've saved or not.
      for (const el of this.$el.elements) {
        if (el.name.startsWith("connection[secrets")) {
          this[this.nameToKey(el.name)] = "";
        }
      }
    },
    setupEventListeners(shown) {
      if (shown) {
        this.resetState();
        window.addEventListener("modal:before-hide", this.beforeHide);
      } else {
        window.removeEventListener("modal:before-hide", this.beforeHide);
      }
    },
    async onSubmit(event) {
      event.preventDefault();
      this.isSubmitting = true;

      let verified = false;
      try {
        verified = await this.runStepsByStepVerification();
      } catch (e) {
        this.apiError = {
          errorMessage:
            "Something went wrong. Please try again or reach out to support.",
        };
      }
      if (verified) {
        this.currentVerificationStep = {
          label: "Updating credentials",
          step: verificationSteps.length + 1,
        };
        await this.updatedCredentials();
      }
      this.currentVerificationStep = null;
      this.isSubmitting = false;
    },
    async runStepsByStepVerification() {
      this.apiError = null;
      const connectionId = this.$el.action.match(/\/connections\/(\d+)/)?.[1];
      const formData = new FormData(this.$el);
      const result = await runStepByStepVerification({
        connectionId,
        verificationSteps: this.verificationSteps,
        formData,
        connectionType: this.connectionType,
        onStepChange: ({ currentStep, stepLabel }) => {
          this.currentVerificationStep = {
            ...this.verificationSteps[currentStep - 1],
            step: currentStep,
            label: stepLabel,
          };
        },
      });
      if (!result.success) {
        this.apiError = result.error;
        return false;
      }
      return true;
    },
    async updatedCredentials() {
      const form = this.$el;
      const formData = new FormData(form);
      const action = form.action;
      const method = formData.get("_method").toUpperCase();
      formData.set("connection_action", "configure");

      const response = await fetch(action, {
        method,
        body: formData,
        headers: { accept: "application/json" },
      });
      const data = await response.json();
      if (!response.ok) {
        this.apiError = {
          errorMessage:
            data.error_message || "There was an error updating the connection",
          errorLink: data.error_link || "",
          curlCommand: data.curl_command || "",
          tokenForCurl: data.token_for_curl || "",
          rawMessage: data.raw_message || "",
        };
      } else {
        this.updateStateOnSuccess();
        this[modalShow] = false;
        window.pushNotification({
          title: "API Credentials Updated",
        });
        if (this.connectionDisabledBadCredentials) {
          // Refresh the page to pull the fresh state if the connection was previously disabled because of credentials
          // This will reset the "disabled" state and put the tabs back, etc. Too hard to manage all of that state
          // from this modal with Alpine.
          setTimeout(() => {
            window.location.reload();
          }, 50);
        }
      }
    },
    copyRedacted() {
      this.copyToClipboard(this.apiError.curlCommand, "redacted");
    },
    copyWithSecret() {
      const curlCommand = this.apiError.curlCommand.replace(
        /REDACTED_.*(TOKEN|PASSWORD|KEY|SECRET)/g,
        this.apiError.tokenForCurl,
      );
      this.copyToClipboard(curlCommand, "secret");
    },
    copyToClipboard(text, type) {
      navigator.clipboard.writeText(text);
      this.copiedWith = type;
      setTimeout(() => (this.copiedWith = null), 2000);
    },
    resetState() {
      // Grab the current value of settings so we can reset it if the user cancels editing
      this.currentSettings = { ...this.settings };
      this.forceClose = false;
      this.showChangedWarning = false;
      this.apiError = false;
      this.copiedWith = null;
      this.currentVerificationStep = null;
      this.isSubmitting = false;
    },
    updateStateOnSuccess() {
      // Update the form field state and the parent settings object to be the
      // current values of the form fields. This keeps the UI in sync with the
      // backends state without needing to refresh the page.
      Object.keys(this.fields).forEach((key) => {
        this.fields[key].initialValue = this[key];
        if (!key.startsWith("connection_secrets")) {
          const settingsKey = key
            .replace("connection_settings_", "")
            .replace(/_+$/, "");
          this.settings[settingsKey] = this[key];
        }
      });
    },
  };

  return withManagedForm({ formRef: "apiCredentialsForm" }, form);
}
